import { createSelector } from 'reselect';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import unescape from 'lodash/unescape';
import memoize from 'lodash/memoize';

import { initProductOptions, productImageOnhover } from './ProductOptionsSelector';
import { selectSizeBasedOnDeviceType } from './ContextSelector';
import { addParams } from '../helpers/Utils/query';

const defaults = {
    productVideos: [],
    stickyProductImage: {},
    productImages: [],
};

/* istanbul ignore next */
const getStickyImage = (store) => store.stickyProductImage || defaults.stickyProductImage;

const getProductImages = memoize((sourceMiniPDP) =>
    createSelector(
        [
            initProductOptions(sourceMiniPDP),
            productImageOnhover(sourceMiniPDP),
            selectSizeBasedOnDeviceType,
        ],
        (productOptions, imageOnHover, deviceSize) => {
            /* istanbul ignore if */
            let productImages = productOptions.productImages || defaults.productImages;

            /* istanbul ignore next */
            if (
                imageOnHover.isSwatchesOnHover &&
                imageOnHover.updateProductImage &&
                !isEmpty(imageOnHover.productImages)
            ) {
                productImages = imageOnHover.productImages;
            }

            let additionaParams = {
                resmode: 'sharp2',
                op_sharpen: 1,
            };

            switch (deviceSize) {
                case 'small':
                    additionaParams = { ...additionaParams, wid: 350, hei: 350 };
                    break;
                case 'medium':
                    additionaParams = { ...additionaParams, wid: 550, hei: 550 };
                    break;
                default:
                    additionaParams = { ...additionaParams, wid: 550, hei: 550 };
            }

            productImages = productImages.map((image) => {
                const { wid: width, hei: height } = additionaParams;
                // eslint-disable-next-line no-param-reassign
                image = {
                    ...image,
                    width,
                    height,
                    url: addParams(unescape(image.url), additionaParams),
                };
                return image;
            });
            return productImages;
        }
    )
);

/* istanbul ignore next */
const getSelectedProductImages = memoize((sourceMiniPDP) =>
    createSelector([getProductImages(sourceMiniPDP)], (productImages) =>
        productImages.map((image) => {
            // eslint-disable-next-line no-param-reassign
            image.key = image.url;
            return image;
        })
    )
);

const getProductVideos = memoize((sourceMiniPDP) =>
    createSelector([initProductOptions(sourceMiniPDP)], (productOptions) => {
        let videos = productOptions.productVideos || defaults.productVideos;

        // filter videos other than brightcove and webcollage
        videos = filter(videos, (video) => {
            if (video.type !== 'Scene7' && !includes(video.url, 'webcollage')) {
                return false;
            }
            return true;
        });

        return videos;
    })
);

export const getSelectedProductVideos = memoize((sourceMiniPDP) =>
    createSelector([getProductVideos(sourceMiniPDP)], (productVideos) => {
        /* istanbul ignore next */
        return productVideos.map((video) => {
            video.key = video.url; // eslint-disable-line no-param-reassign
            video.isVideo = true; // eslint-disable-line no-param-reassign
            video.icon = 'play'; // eslint-disable-line no-param-reassign
            return video;
        });
    })
);

export const getProductImagesAndVideos = memoize((sourceMiniPDP = false) =>
    createSelector(
        [getSelectedProductImages(sourceMiniPDP), getSelectedProductVideos(sourceMiniPDP)],
        (productImages, productVideos) => productImages.concat(productVideos)
    )
);

export const selectFilmstripImagesAndVideo = memoize((sourceMiniPDP = false) =>
    createSelector([getProductImagesAndVideos(sourceMiniPDP)], (productImages) => {
        // eslint-disable-next-line no-param-reassign
        productImages = productImages.map((image, index) => {
            if (!image.isVideo && index !== 0) {
                // eslint-disable-next-line no-param-reassign
                image = {
                    ...image,
                    url: addParams(image.url, {
                        wid: 150,
                        hei: 150,
                    }),
                };
            }
            return image;
        });
        return productImages;
    })
);

/* istanbul ignore next */
export const selectSticky = createSelector(
    [getStickyImage],
    (stickyProductImage) => !!stickyProductImage.sticky
);

/* istanbul ignore next */
export const selectPartialSticky = createSelector(
    [getStickyImage],
    (stickyProductImage) => !!stickyProductImage.partialSticky
);

export default getSelectedProductImages;
